/**
 * A8 (2026-06-04) — Mission Dossier Export.
 *
 * Generates a styled HTML document representing the campaign session as
 * a M.I.6 mission dossier. The HTML is print-first (`@media print` +
 * page-break controls) so the browser's native print-to-PDF dialog
 * produces a clean dossier without needing a bundled PDF library.
 *
 * Slice A scope:
 *   - generateDossierHTML(input) → standalone HTML string with inline CSS
 *   - Redaction: TAROT operatives whose name is NOT in `recognizedNpcs`
 *     get their name replaced with ████████ blocks in body text + NPC
 *     register listings
 *   - Classification stamp + mission header + roll history + NPC register
 *
 * Slice B will add:
 *   - Server-side weasyprint backend at /api/modules/briefing-room/
 *     missions/{id}/export?format=pdf (module-host mode only)
 *   - Per-tenant brand override (replaces M.I.6 with tenant-named agency)
 *   - IndexedDB caching for offline export
 */

export interface DossierRoll {
  paragraph: number;
  skill_name?: string;
  ease_factor: string;
  primary_chance: number;
  success_chance: number;
  d100_roll: number;
  quality_rating: number | null;
  quality_name: string;
}

export interface DossierNpc {
  id: string;
  name: string;
  /** True when this NPC is from TAROT (the antagonist organization). */
  isTarot?: boolean;
  description?: string;
}

export interface PriorEncounter {
  npcId: string;
  npcName: string;
  /** Canonical scenario id from the backend cross_scenario aggregate
   *  (e.g. `docs/dr_no_VG35000.md`). Null when the NPC was carried over
   *  but the source scenario could not be resolved (defensive). */
  firstScenarioId: string | null;
  /** ISO timestamp of first effective recognition across all scenarios.
   *  Null when no recognition has been recorded yet (shouldn't happen
   *  in practice since carry-over implies recognition, but defensive). */
  firstRecognizedAt: string | null;
  /** Cumulative count of GM gm_override applications on this NPC across
   *  all scenarios in the campaign. Zero suppresses the override note. */
  overrideCount: number;
}

export interface DossierExportInput {
  /** Mission display name. */
  missionName: string;
  /** Mission file number — defaults to '007-XXX-XX' when omitted. */
  fileNumber?: string;
  /** Case officer name; redaction-bar-wrapped in output for security. */
  caseOfficer?: string;
  /** Resolved roll history. */
  rolls: DossierRoll[];
  /** All NPCs in the campaign. TAROT entries are redacted unless
   *  listed in `recognizedNpcs`. */
  npcs: DossierNpc[];
  /** Set of NPC IDs whose Fame Recognition roll has been passed —
   *  these names appear unredacted in the export. */
  recognizedNpcs: ReadonlySet<string>;
  /** A8r Dossier-Enhancement (2026-06-04): TAROT NPCs carried in from
   *  prior scenarios in this campaign, with first-scenario + first-date
   *  context. Omit or pass empty array to suppress the section. */
  priorEncounters?: PriorEncounter[];
  /** Optional export timestamp; defaults to a placeholder so tests are
   *  deterministic without injecting a clock. */
  generatedAt?: string;
}

const REDACTION_GLYPH = '█'.repeat(8); // ████████ (8 full blocks)
const FILE_NO_DEFAULT = '007-XXX-XX';

function redactName(npc: DossierNpc, recognizedNpcs: ReadonlySet<string>): string {
  if (npc.isTarot && !recognizedNpcs.has(npc.id)) {
    return REDACTION_GLYPH;
  }
  return npc.name;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderClassificationHeader(missionName: string, fileNumber: string): string {
  return `
    <header class="dossier-classification">
      <div class="dossier-classification-banner">
        TOP SECRET · M.I.6 EYES ONLY · CLASSIFICATION ORANGE
      </div>
      <div class="dossier-classification-meta">
        <div>MISSION: ${escapeHtml(missionName.toUpperCase())} · FILE NO. ${escapeHtml(fileNumber)}</div>
        <div>CASE OFFICER: ${REDACTION_GLYPH} · APPROVED: M</div>
      </div>
    </header>
  `.trim();
}

function renderRollLog(rolls: DossierRoll[]): string {
  if (rolls.length === 0) {
    return '<section class="dossier-section"><h2>Roll Log</h2><p>(no rolls recorded)</p></section>';
  }
  const rows = rolls
    .map(
      (r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>¶${r.paragraph}</td>
        <td>${escapeHtml(r.skill_name ?? '—')}</td>
        <td>EF ${escapeHtml(r.ease_factor)}</td>
        <td>SC ${r.success_chance}</td>
        <td>d100 ${r.d100_roll}</td>
        <td>${escapeHtml(r.quality_name)}</td>
      </tr>
    `,
    )
    .join('');
  return `
    <section class="dossier-section dossier-roll-log">
      <h2>Roll Log</h2>
      <table>
        <thead>
          <tr><th>#</th><th>¶</th><th>Skill</th><th>EF</th><th>SC</th><th>Roll</th><th>Quality</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>
  `.trim();
}

function renderPriorEncounters(
  priorEncounters: PriorEncounter[] | undefined,
): string {
  if (!priorEncounters || priorEncounters.length === 0) {
    return '';
  }
  const rows = priorEncounters
    .map((p) => {
      const scenarioLabel = p.firstScenarioId
        ? p.firstScenarioId.replace(/^docs\//, '').replace(/\.md$/, '')
        : '(unknown scenario)';
      const dateLabel = p.firstRecognizedAt
        ? p.firstRecognizedAt.slice(0, 10)
        : '(unknown date)';
      const overrideNote =
        p.overrideCount > 0
          ? ` · ${p.overrideCount} GM override${p.overrideCount === 1 ? '' : 's'}`
          : '';
      return `
        <tr>
          <td>${escapeHtml(p.npcName)}</td>
          <td><code>${escapeHtml(scenarioLabel)}</code></td>
          <td>${escapeHtml(dateLabel)}</td>
          <td>${escapeHtml(overrideNote)}</td>
        </tr>
      `;
    })
    .join('');
  return `
    <section class="dossier-section dossier-prior-encounters">
      <h2>Prior Encounters</h2>
      <table>
        <thead>
          <tr><th>Operative</th><th>First Scenario</th><th>First Recognized</th><th>Notes</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="dossier-redaction-note">
        Cross-scenario carry: these operatives were positively identified in
        prior briefings within this campaign and therefore appear unredacted in
        the Personnel Register above.
      </p>
    </section>
  `.trim();
}

function renderNpcRegister(
  npcs: DossierNpc[],
  recognizedNpcs: ReadonlySet<string>,
): string {
  if (npcs.length === 0) {
    return '<section class="dossier-section"><h2>Personnel Register</h2><p>(no NPCs in this mission)</p></section>';
  }
  const rows = npcs
    .map((npc) => {
      const name = redactName(npc, recognizedNpcs);
      const desc = npc.description ?? '';
      const isRedacted = name === REDACTION_GLYPH;
      const descCell = isRedacted ? REDACTION_GLYPH : escapeHtml(desc);
      const affiliation = npc.isTarot ? 'TAROT' : 'M.I.6 / civilian';
      return `
        <tr${isRedacted ? ' class="dossier-redacted"' : ''}>
          <td>${escapeHtml(name)}</td>
          <td>${affiliation}</td>
          <td>${descCell}</td>
        </tr>
      `;
    })
    .join('');
  return `
    <section class="dossier-section dossier-npc-register">
      <h2>Personnel Register</h2>
      <table>
        <thead>
          <tr><th>Name</th><th>Affiliation</th><th>Description</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="dossier-redaction-note">
        Names obscured with ${REDACTION_GLYPH.substring(0, 4)} have not yet been positively identified
        in-mission; details remain classified pending Fame Recognition.
      </p>
    </section>
  `.trim();
}

const DOSSIER_CSS = `
  @media print {
    @page { size: A4 portrait; margin: 18mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  body {
    font-family: 'Courier New', Courier, monospace;
    background: #f5f5f0;
    color: #1a1a1a;
    margin: 0;
    padding: 20mm 18mm;
  }
  .dossier-classification {
    border: 2px solid #8b0000;
    padding: 10px 14px;
    margin-bottom: 18px;
    background: #fff;
  }
  .dossier-classification-banner {
    text-align: center;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: #8b0000;
    border-bottom: 1px solid #8b0000;
    padding-bottom: 4px;
    margin-bottom: 6px;
  }
  .dossier-classification-meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    color: #444;
  }
  .dossier-section {
    margin: 22px 0;
    page-break-inside: avoid;
  }
  .dossier-section h2 {
    font-size: 1.1rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    border-bottom: 1px solid #8b0000;
    padding-bottom: 4px;
    color: #1a3a5c;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
  }
  th, td {
    text-align: left;
    padding: 4px 8px;
    border-bottom: 1px solid #ccc;
    font-size: 0.85rem;
  }
  th {
    background: #ebebe5;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  tr.dossier-redacted td {
    background: #2a2a2a;
    color: #f5f5f0;
  }
  .dossier-redaction-note {
    font-size: 0.75rem;
    color: #888;
    font-style: italic;
    margin-top: 6px;
  }
`.trim();

export function generateDossierHTML(input: DossierExportInput): string {
  const fileNumber = input.fileNumber ?? FILE_NO_DEFAULT;
  const generatedAt = input.generatedAt ?? 'UNDATED';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>M.I.6 Mission Dossier — ${escapeHtml(input.missionName)}</title>
  <style>${DOSSIER_CSS}</style>
</head>
<body>
  ${renderClassificationHeader(input.missionName, fileNumber)}
  ${renderRollLog(input.rolls)}
  ${renderPriorEncounters(input.priorEncounters)}
  ${renderNpcRegister(input.npcs, input.recognizedNpcs)}
  <footer class="dossier-section">
    <p class="dossier-redaction-note">
      Generated ${escapeHtml(generatedAt)} · This document is the property of Her Majesty's Government.
    </p>
  </footer>
</body>
</html>`;
}

/**
 * Opens the generated dossier in a new browser tab for the user to
 * print-to-PDF (or save). Returns the new window's reference for
 * tests; returns null when called in a non-browser environment OR
 * when window.open is blocked by a popup blocker.
 */
export function openDossierInNewWindow(
  input: DossierExportInput,
  win?: Window,
): Window | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const target = win ?? window;
  const html = generateDossierHTML(input);
  const popup = target.open('', '_blank', 'noopener,noreferrer');
  if (!popup) {
    return null;
  }
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  return popup;
}
